import { AMSControlAction } from "./printerResponse";
import { NumberRange, SpeedLevel } from "./printerResponse";
import { validateCommandParams } from "./commandSchemas";

// Re-export AMSControlAction so existing code doesn't break
export { AMSControlAction };

// Command Categories
export type CommandCategory = "info" | "pushing" | "system" | "print";

// Light Control Types
export enum LightEffect {
  ON = "on",
  OFF = "off",
  FLASHING = "flashing",
}

export enum LightNode {
  CHAMBER = "chamber_light",
  WORK = "work_light",
}

export interface LightOptions {
  onTime?: number;
  offTime?: number;
  loops?: number;
  interval?: number;
}

// Camera Control Types
export enum CameraOperation {
  RECORD = "ipcam_record_set",
  TIMELAPSE = "ipcam_timelapse",
  RESOLUTION = "ipcam_resolution_set",
}

// Fan Control Types
export enum FanType {
  PART_COOLING = 1,
  AUXILIARY = 2,
  CHAMBER = 3,
}

// Axis Control Types
export type Axis = "X" | "Y" | "Z" | "E";

// Valid Commands
export const VALID_COMMANDS = [
  "updateLight",
  "updateFan",
  "updateSpeed",
  "updateTemp",
  "updateState",
  "gcode",
  "resume",
  "ams_control",
  "refreshPrinterState",
  "ams_filament_settings",
  "loadFilament",
  "unloadFilament",
  "axisControl",
  "autoLevel",
  "goHome",
  "cleanPrintError",
  "powerLossRecovery",
  "camera",
  "taskAbort",
  "taskCancel",
  // High-priority HMS & system commands
  "hmsResume",
  "hmsIgnore",
  "hmsStop",
  "stopBuzzer",
  "getAccessCode",
  // Medium-priority AMS commands
  "amsUserSettings",
  "amsCalibrate",
  "amsRefreshRfid",
  "amsRefreshRfid2",
  "amsSelectTray",
  "amsDryingStop",
  // Medium-priority other commands
  "setNozzleTempNew",
  "xyzAbsoluteMode",
  "printingOptions",
  // Light testing command
  "testLightSequence",
] as const;

export type CommandType = (typeof VALID_COMMANDS)[number];

// Command Parameter Types
export type CommandParams = {
  updateLight: {
    light: LightNode;
    mode: LightEffect;
    loopOptions?: LightOptions;
  };
  updateFan: {
    fan: FanType;
    speed: NumberRange<0, 100>;
  };
  updateSpeed: {
    speed: SpeedLevel;
  };
  updateTemp: {
    part: "bed" | "nozzle" | "chamber";
    temperature: NumberRange<0, 300>;
  };
  updateState: {
    state: "pause" | "resume" | "stop";
  };
  gcode: {
    gcodes: string[];
  };
  resume: {
    cleanPrintError?: boolean;
    printError?: number;
    subtaskId?: string;
  };
  taskAbort: Record<string, never>;
  taskCancel: {
    jobId: string;
  };
  ams_control: {
    param: AMSControlAction;
  };
  refreshPrinterState: Record<string, never>;
  ams_filament_settings: {
    ams_id: number;
    slot_id: number;
    tray_id: number;
    filament_id: string;
    setting_id: string;
    color: string;
    filament_type: string;
    nozzle_temp_min: number;
    nozzle_temp_max: number;
  };
  loadFilament: {
    trayId: number;
    oldTemp?: number;
    newTemp?: number;
  };
  unloadFilament: {
    oldTemp?: number;
    newTemp?: number;
  };
  axisControl: {
    axis: Axis;
    distance: number;
    unit?: number;
    speed?: number;
  };
  autoLevel: Record<string, never>;
  goHome: Record<string, never>;
  cleanPrintError: {
    subtaskId: string;
    printError: number;
  };
  powerLossRecovery: {
    action: "resume" | "stop";
    subtaskId: string;
    printError: number;
  };
  camera: {
    operation: CameraOperation;
    value: boolean | string;
  };
  // High-priority HMS & system commands
  hmsResume: {
    errorCode: string;
    jobId: string;
  };
  hmsIgnore: {
    errorCode: string;
    jobId: string;
  };
  hmsStop: {
    errorCode: string;
    jobId: string;
  };
  stopBuzzer: Record<string, never>;
  getAccessCode: Record<string, never>;
  // Medium-priority AMS commands
  amsUserSettings: {
    amsId: number;
    startupReadOption: boolean;
    trayReadOption: boolean;
    calibrateRemainFlag: boolean;
  };
  amsCalibrate: {
    amsId: number;
  };
  amsRefreshRfid: {
    trayId: string;
  };
  amsRefreshRfid2: {
    amsId: number;
    slotId: number;
  };
  amsSelectTray: {
    trayId: string;
  };
  amsDryingStop: Record<string, never>;
  // Medium-priority other commands
  setNozzleTempNew: {
    extruderIndex: number;
    targetTemp: NumberRange<0, 300>;
  };
  xyzAbsoluteMode: Record<string, never>;
  printingOptions: {
    autoRecovery: boolean;
  };
  // Light testing command
  testLightSequence: {
    sequence: [boolean, boolean, boolean, boolean, boolean];
    chamberLight: boolean;
    workLight: boolean;
    loops?: number;
  };
};

// Command Response Types
export interface CommandResponse {
  sequence_id?: string;
  status?: "success" | "error";
  error?: string;
}

// Factory Functions
export const createCommand = <T extends CommandType>(
  command: T,
  params: CommandParams[T]
): { command: T; params: CommandParams[T] } => ({
  command,
  params,
});

// Helper Functions
export const isValidCommand = (command: string): command is CommandType =>
  VALID_COMMANDS.includes(command as CommandType);

// Re-export the Zod validator
export { validateCommandParams };
